import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  pointerWithin,
  type DragStartEvent,
  type DragEndEvent,
} from "@dnd-kit/core";
import { ArrowLeftIcon, Square2StackIcon } from "@heroicons/react/24/outline";
import PageHeader from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/Button";
import toast from "react-hot-toast";
import { activitiesApi } from "../../api/activities";
import {
  computeClockwiseSlots,
  layoutKerchief,
  type SlotPos,
} from "../../utils/kerchiefSlots";
import type { Activity } from "../../types";

// The 26 printed slots, clockwise: 1–11 across the top, 12–18 down the
// right edge, 19 at the tip, 20–26 back up the left edge.
const SLOTS = computeClockwiseSlots();

// ─── Badge visuals ────────────────────────────────────────────────────────────

function BadgeCircle({
  activity,
  order,
  onCloth = false,
}: {
  activity: Activity;
  order?: number;
  /** True when rendered on the red cloth — light label text with a shadow. */
  onCloth?: boolean;
}) {
  const archived = !!activity.archivedAt;
  return (
    <div className="flex flex-col items-center w-16 select-none">
      <div className="relative">
        <div
          className={`w-11 h-11 rounded-full bg-white border-2 flex items-center justify-center overflow-hidden shadow-sm ${
            archived ? "border-stone-400 border-dashed grayscale opacity-75" : "border-stone-800"
          }`}
          title={archived ? `${activity.name} (archived)` : activity.name}
        >
          {activity.thumbnailUrl ? (
            <img
              src={activity.thumbnailUrl}
              alt={activity.name}
              className="w-full h-full object-cover"
              draggable={false}
            />
          ) : (
            <Square2StackIcon className="w-6 h-6 text-stone-500" />
          )}
        </div>
        {order != null && (
          <span className="absolute -top-1.5 -right-1.5 w-[18px] h-[18px] rounded-full bg-stone-800 text-white text-[9px] font-bold flex items-center justify-center leading-none">
            {order}
          </span>
        )}
      </div>
      <span
        className={`mt-0.5 text-[9px] font-semibold text-center leading-tight line-clamp-2 ${
          archived ? "italic " : ""
        }${onCloth ? "text-stone-100 [text-shadow:0_1px_2px_rgba(0,0,0,0.6)]" : "text-stone-600"}`}
      >
        {activity.name}
      </span>
    </div>
  );
}

function DraggableBadge({ activity, order, onCloth }: { activity: Activity; order?: number; onCloth?: boolean }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `act-${activity.id}`,
  });
  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className={`cursor-grab active:cursor-grabbing touch-none ${isDragging ? "opacity-30" : ""}`}
    >
      <BadgeCircle activity={activity} order={order} onCloth={onCloth} />
    </div>
  );
}

function WriteInTray({ writeIns }: { writeIns: Activity[] }) {
  // Must be its own component: useDroppable needs to run inside the
  // DndContext rendered by the page.
  const { setNodeRef, isOver } = useDroppable({ id: "tray" });
  return (
    <div
      ref={setNodeRef}
      className={`max-w-5xl mx-auto rounded-xl border-2 border-dashed p-4 min-h-[96px] transition-colors ${
        isOver ? "border-amber-400 bg-amber-50" : "border-stone-300 bg-stone-50"
      }`}
    >
      <p className="text-xs font-semibold text-stone-500 mb-2">
        Write-ins — not printed on the current kerchief (hand-written when stamping; sorted after
        printed activities in reports)
      </p>
      <div className="flex flex-wrap gap-3">
        {writeIns.length === 0 ? (
          <span className="text-xs text-stone-400">
            Drag activities here if they aren't printed on the cloth.
          </span>
        ) : (
          writeIns.map((a) => <DraggableBadge key={a.id} activity={a} />)
        )}
      </div>
    </div>
  );
}

function SlotDroppable({ index, pos, children }: { index: number; pos: SlotPos; children?: React.ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({ id: `slot-${index}` });
  // The centering transform lives on the OUTER div: dnd-kit measures droppable
  // rects ignoring the element's own transform, so a transform directly on the
  // droppable shifts its hit-area half a badge off its visual position.
  return (
    <div
      className="absolute -translate-x-1/2 -translate-y-1/2"
      style={{ left: `${pos.left}%`, top: `${pos.top}%` }}
    >
      <div
        ref={setNodeRef}
        className={`rounded-xl p-0.5 transition-shadow ${isOver ? "ring-2 ring-amber-400 bg-amber-400/20" : ""}`}
      >
        {children ?? (
          <div className="w-16 flex flex-col items-center">
            <div className="w-11 h-11 rounded-full border-2 border-dashed border-red-300/70 flex items-center justify-center text-[10px] font-bold text-red-200/80">
              {index + 1}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function KerchiefLayout() {
  // One entry per physical slot; null = empty slot.
  const [printed, setPrinted] = useState<(Activity | null)[]>([]);
  const [writeIns, setWriteIns] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [activeId, setActiveId] = useState<number | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 6 } })
  );

  const load = useCallback(() => {
    setLoading(true);
    // Include archived: retired activities are often still printed on the
    // current physical kerchief, so they hold a slot in the layout.
    activitiesApi
      .list(true)
      .then((all) => {
        // list() is already in kerchief (sort_order) order; a printed
        // activity's sort_order is its slot. Collisions and overflow get
        // re-placed (saving persists the fix).
        const layout = layoutKerchief(all);
        setPrinted(layout.slots);
        setWriteIns(layout.writeIns);
        setDirty(layout.adjusted);
      })
      .catch(() => toast.error("Failed to load activities"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleDragStart = (e: DragStartEvent) => {
    setActiveId(Number(String(e.active.id).replace("act-", "")));
  };

  const handleDragEnd = (e: DragEndEvent) => {
    setActiveId(null);
    const overId = e.over?.id != null ? String(e.over.id) : null;
    if (!overId) return;
    const id = Number(String(e.active.id).replace("act-", ""));
    const fromSlot = printed.findIndex((a) => a?.id === id);
    const item = fromSlot >= 0 ? printed[fromSlot] : writeIns.find((a) => a.id === id);
    if (!item) return;

    if (overId === "tray") {
      if (fromSlot < 0) return;
      // Leaves the slot empty — no other badge moves.
      setPrinted(printed.map((a, i) => (i === fromSlot ? null : a)));
      setWriteIns([...writeIns, item]);
      setDirty(true);
      return;
    }

    if (overId.startsWith("slot-")) {
      const target = Number(overId.slice(5));
      if (target === fromSlot) return;
      const occupant = printed[target];
      const next = [...printed];
      next[target] = item;
      if (fromSlot >= 0) {
        // Slot to slot: swap with whatever is there (possibly nothing).
        next[fromSlot] = occupant;
      } else {
        // Tray to slot: a displaced badge goes back to the tray.
        setWriteIns([...writeIns.filter((a) => a.id !== id), ...(occupant ? [occupant] : [])]);
      }
      setPrinted(next);
      setDirty(true);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await activitiesApi.reorder(printed.map((a) => a?.id ?? null), writeIns.map((a) => a.id));
      setDirty(false);
      toast.success("Kerchief layout saved");
    } catch {
      toast.error("Failed to save kerchief layout");
    } finally {
      setSaving(false);
    }
  };

  const activeItem =
    activeId != null
      ? printed.find((a) => a?.id === activeId) ?? writeIns.find((a) => a.id === activeId) ?? null
      : null;


  return (
    <div>
      <PageHeader
        title="Kerchief Layout"
        subtitle="Arrange activities to match the physical kerchief"
        actions={
          <>
            <Button variant="outline" onClick={load} disabled={!dirty || saving}>
              Reset
            </Button>
            <Button variant="primary" onClick={handleSave} disabled={!dirty} loading={saving}>
              Save Layout
            </Button>
          </>
        }
      />

      <div className="p-6 lg:p-8 space-y-6">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <Link
            to="/admin/activities"
            className="inline-flex items-center gap-1.5 text-sm text-stone-500 hover:text-forest-700"
          >
            <ArrowLeftIcon className="w-4 h-4" />
            Back to Manage Activities
          </Link>
          <p className="text-xs text-stone-400">
            26 printed slots, numbered clockwise from the top-left: 1–11 across the top, 12–18
            down the right edge, 19 at the point, 20–26 back up the left edge. Reports stamp in
            this order. Archived activities appear dimmed with a dashed ring — they can still
            hold a printed slot.
          </p>
        </div>

        {loading ? (
          <div className="text-sm text-stone-400 py-16 text-center">Loading activities…</div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={pointerWithin}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            {/* The cloth */}
            <div className="max-w-5xl mx-auto">
              <div className="relative w-full" style={{ paddingBottom: "56%" }}>
                {/* Triangle */}
                <svg
                  viewBox="0 0 100 56"
                  preserveAspectRatio="none"
                  className="absolute inset-0 w-full h-full"
                >
                  <polygon points="0,0 100,0 50,53" fill="#c62828" />
                  <polygon points="0,0 100,0 50,53" fill="none" stroke="#8e1f1f" strokeWidth="0.4" />
                </svg>
                {/* CH-style center mark */}
                <div className="absolute left-1/2 top-[36%] -translate-x-1/2 -translate-y-1/2 text-red-900/50 font-display font-bold text-3xl pointer-events-none">
                  CH
                </div>

                {SLOTS.map((pos, i) => (
                  <SlotDroppable key={i} index={i} pos={pos}>
                    {printed[i] ? <DraggableBadge activity={printed[i]} order={i + 1} onCloth /> : undefined}
                  </SlotDroppable>
                ))}
              </div>
            </div>

            {/* Write-ins tray */}
            <WriteInTray writeIns={writeIns} />

            <DragOverlay>
              {activeItem ? <BadgeCircle activity={activeItem} /> : null}
            </DragOverlay>
          </DndContext>
        )}
      </div>
    </div>
  );
}
